import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { Loading, MessagesSearch } from '@swyftx/aviary/icons/outlined';
import { EnhancedTable, EnhancedTableData } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginInstrument } from '@swyftx/margin/build/types';

import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

import { MarginInstrumentsTableData } from './MarginInstrumentTable.types';
import { useMarginInstrumentsTable } from './useMarginInstrumentsTable';

type Props = {
  search?: string;
  onSelectItem: () => void;
};

const MarginInstrumentTable: React.FC<Props> = ({ search, onSelectItem }) => {
  const { headers, data, onSort, initialSort, isLoading } = useMarginInstrumentsTable({ search });
  const { navigate } = useNavigateRoute();
  const isDesktopView = useTailwindBreakpoint('md', true);
  const { t } = useTranslation('margin');
  const { getAssetByUuid } = useMarkets();
  const { selectedMarket } = useMarginInstruments();

  const onClickItem = useCallback(
    (item: EnhancedTableData<MarginInstrumentsTableData, MarginInstrument>) => {
      const asset = getAssetByUuid(item.value?.secondaryAssetUuid);
      if (!asset) return;
      navigate(NavigationURLs.MarginTradeAsset, { pathParams: { asset: asset.code } });
      onSelectItem();
    },
    [getAssetByUuid, navigate, onSelectItem],
  );

  return (
    <EnhancedTable<MarginInstrumentsTableData, MarginInstrument>
      data-container='orders-history-table'
      headers={headers}
      data={data}
      onSort={onSort}
      onClickItem={onClickItem}
      sort={initialSort}
      hideHeader={!isDesktopView}
      headerClassName='bg-color-background-surface-primary'
      inset
      rowClassName='h-16'
      highlightSelectedRow
      value={selectedMarket}
      itemsPerPage={25}
      lazyLoad
    >
      {isLoading ? (
        <FlexLayout className='h-full w-full p-24' alignItems='center' justifyContent='center'>
          <Loading className='animate-spin' />
        </FlexLayout>
      ) : (
        <FlexLayout
          className='h-full w-full p-24'
          alignItems='center'
          justifyContent='center'
          direction='column'
          spacing={8}
        >
          <MessagesSearch className='text-color-text-accent' />
          <Heading size='h5'>{t('instruments.table.empty.title')}</Heading>
          <Body color='secondary' size='small'>
            {t('instruments.table.empty.content')}
          </Body>
        </FlexLayout>
      )}
    </EnhancedTable>
  );
};

export { MarginInstrumentTable };
